<template>
  <!-- 登录页：不显示侧边栏/顶栏/对话框，只渲染登录卡片 -->
  <div v-if="isLogin" class="app-login-host">
    <router-view />
  </div>
  <!-- 其他页面：完整布局 -->
  <div v-else class="app-shell">
    <!-- 侧边栏 -->
    <aside class="app-sidebar">
      <div class="app-brand">
        <div class="app-logo">花</div>
        <div>
          <strong>花粉监测平台</strong>
          <span>数据管理与预警</span>
        </div>
      </div>
      <nav class="app-nav">
        <router-link
          v-for="item in filteredNav"
          :key="item.path"
          :to="item.path"
          class="app-nav-item"
          :class="{ active: isActive(item.path) }"
        >
          <i class="app-nav-icon" :style="{ background: item.color }"></i>
          {{ item.label }}
        </router-link>
      </nav>
      <div class="app-sidebar-foot">
        <div class="foot-user">
          <i class="el-icon-user-solid"></i>
          <span>{{ user ? user.name : '未登录' }}</span>
        </div>
        <div class="foot-meta">v1.0.0 · 数据接口已就绪</div>
      </div>
    </aside>

    <!-- 主区域 -->
    <section class="app-main">
      <header class="app-topbar">
        <div class="app-breadcrumb">
          管理端 / <strong>{{ currentTitle }}</strong>
        </div>
        <div class="app-context">
          <span class="ctx-label">数据范围</span>
          <el-select v-model="scope" size="small" style="width: 140px" @change="onScopeChange">
            <el-option label="全部区域" value="全部区域" />
            <el-option v-for="r in regions" :key="r" :label="r" :value="r" />
          </el-select>
          <el-tooltip :content="theme === 'dark' ? '切换到正常模式' : '切换到暗色模式'" placement="bottom">
            <el-button size="small" class="theme-toggle" @click="toggleTheme">
              <i :class="theme === 'dark' ? 'el-icon-sunny' : 'el-icon-moon'"></i>
              {{ theme === 'dark' ? '正常' : '暗色' }}
            </el-button>
          </el-tooltip>
          <span class="ctx-user">
            <i class="el-icon-user-solid"></i>
            {{ user ? user.name : '' }}
            <el-tag
              v-if="user && user.roleName"
              size="mini"
              class="role-tag"
              :type="roleTagType"
            >{{ user.roleName }}</el-tag>
          </span>
          <el-dropdown trigger="click" @command="onUserCommand">
            <i class="el-icon-arrow-down ctx-more"></i>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item disabled>账号：{{ user ? user.username : '-' }}</el-dropdown-item>
              <el-dropdown-item disabled>角色：{{ user ? user.roleName : '-' }}</el-dropdown-item>
              <el-dropdown-item command="roleChange" icon="el-icon-user">申请变更身份</el-dropdown-item>
              <el-dropdown-item divided command="logout" icon="el-icon-switch-button">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <main class="app-content">
        <router-view />
      </main>
    </section>

    <!-- 申请变更身份对话框 -->
    <el-dialog title="申请变更身份" :visible.sync="roleChangeVisible" width="440px" append-to-body>
      <el-form :model="roleChangeForm" label-width="90px" size="medium">
        <el-form-item label="当前身份">
          <el-input :value="user ? user.roleName : '-'" disabled />
        </el-form-item>
        <el-form-item label="申请身份">
          <el-select v-model="roleChangeForm.targetRole" placeholder="请选择要申请的身份" style="width: 100%">
            <el-option label="系统管理员（全部功能）" value="SYSTEM_ADMIN" />
            <el-option label="设备运维人员（设备/预警）" value="DEVICE_OPERATOR" />
            <el-option label="数据分析人员（数据/报表）" value="DATA_ANALYST" />
          </el-select>
        </el-form-item>
        <el-form-item label="申请理由">
          <el-input type="textarea" :rows="3" v-model="roleChangeForm.reason" placeholder="说明申请变更身份的理由" />
        </el-form-item>
        <el-alert type="warning" :closable="false" show-icon>
          身份变更需平台管理员审批，提交后请等待审批结果（管理员可在审计日志查看申请）。
        </el-alert>
      </el-form>
      <span slot="footer">
        <el-button @click="roleChangeVisible = false">取 消</el-button>
        <el-button type="primary" :loading="roleChangeLoading" @click="onApplyRole">提交申请</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { applyRoleChange, getStations } from '@/api'
// 全量菜单 + 所需权限码
const ALL_NAV = [
  { path: '/', label: '实时态势', color: '#1d68c9', perm: 'dashboard:view' },
  { path: '/devices', label: '设备与站点', color: '#198754', perm: 'device:view' },
  { path: '/observations', label: '历史数据', color: '#0d9488', perm: 'observation:view' },
  { path: '/alerts', label: '预警中心', color: '#b86d00', perm: 'alert:view' },
  { path: '/reports', label: '报表中心', color: '#7c3aed', perm: 'report:view' },
  { path: '/recycle', label: '回收站', color: '#dc2626', perm: 'recycle:view' },
  { path: '/system', label: '系统管理', color: '#475569', perm: 'system:view' }
]

export default {
  name: 'App',
  data() {
    return {
      navItems: ALL_NAV,
      roleChangeVisible: false,
      roleChangeLoading: false,
      roleChangeForm: { targetRole: '', reason: '' },
      regions: []
    }
  },
  mounted() {
    this.loadRegions()
  },
  computed: {
    isLogin() {
      return this.$route.path === '/login'
    },
    currentTitle() {
      return this.$route.meta && this.$route.meta.title ? this.$route.meta.title : '实时态势'
    },
    // 按当前用户权限过滤菜单
    filteredNav() {
      const perms = (this.user && this.user.permissions) || []
      return this.navItems.filter(item => perms.includes(item.perm))
    },
    scope: {
      get() { return this.$store.state.scope },
      set(v) { this.$store.commit('SET_SCOPE', v) }
    },
    theme: {
      get() { return this.$store.state.theme },
      set(v) { this.$store.commit('SET_THEME', v) }
    },
    user() {
      return this.$store.state.user
    },
    roleTagType() {
      const code = this.user && this.user.roleCode
      if (code === 'ROLE_ADMIN') return 'danger'
      if (code === 'ROLE_MONITOR') return 'success'
      if (code === 'ROLE_AUDITOR') return 'warning'
      return 'info'
    }
  },
  methods: {
    isActive(path) {
      return this.$route.path === path
    },
    // 从站点接口拉取真实区域（region）作为「数据范围」下拉选项
    async loadRegions() {
      try {
        const res = await getStations()
        const list = res.data || []
        const set = new Set(list.map(s => s.region).filter(Boolean))
        this.regions = [...set]
      } catch (e) {
        // 接口失败时保留默认「全部区域」
      }
    },
    onScopeChange() {
      // 通知各页面刷新（通过全局事件）
      this.$bus && this.$bus.$emit('scope-change', this.scope)
    },
    // 切换暗色 / 正常模式：写 html 属性 + 持久化 + 广播图表重绘
    toggleTheme() {
      const next = this.theme === 'dark' ? 'light' : 'dark'
      this.theme = next
      document.documentElement.setAttribute('data-theme', next)
      localStorage.setItem('pollen_theme', next)
      this.$bus && this.$bus.$emit('theme-change', next)
    },
    onUserCommand(cmd) {
      if (cmd === 'roleChange') {
        this.roleChangeVisible = true
      } else if (cmd === 'logout') {
        this.$confirm('确定退出登录吗？', '提示', { type: 'warning' }).then(() => {
          this.$store.dispatch('logout')
          this.$router.replace('/login')
          this.$message.success('已退出登录')
        }).catch(() => {})
      }
    },
    async onApplyRole() {
      if (!this.user || !this.user.id) {
        this.$message.error('请先登录')
        return
      }
      if (!this.roleChangeForm.targetRole) {
        this.$message.error('请选择要申请的身份')
        return
      }
      this.roleChangeLoading = true
      try {
        await applyRoleChange({
          userId: this.user.id,
          targetRole: this.roleChangeForm.targetRole,
          reason: this.roleChangeForm.reason
        })
        this.$message.success('申请已提交，请等待平台管理员审批')
        this.roleChangeVisible = false
        this.roleChangeForm = { targetRole: '', reason: '' }
      } catch (e) {
        // 错误由拦截器提示
      } finally {
        this.roleChangeLoading = false
      }
    }
  }
}
</script>

<style scoped>
.app-shell {
  display: flex;
  min-height: 100vh;
  background: var(--p-bg);
}

/* ---------- 侧边栏 ---------- */
.app-sidebar {
  width: 218px;
  flex-shrink: 0;
  background: var(--p-surface);
  border-right: 1px solid var(--p-line);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
}
.app-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 22px 18px 20px;
  border-bottom: 1px solid var(--p-line);
}
.app-logo {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1d68c9, #4f9df7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
}
.app-brand strong { display: block; font-size: 15px; font-weight: 600; letter-spacing: 0.02em; }
.app-brand span { color: var(--p-muted); font-size: 12px; }

.app-nav {
  padding: 14px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.app-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-radius: 8px;
  color: var(--p-ink);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.18s ease;
}
.app-nav-item:hover {
  background: var(--p-subtle);
}
.app-nav-item.active {
  background: var(--p-blue-soft);
  color: var(--p-blue);
  font-weight: 500;
}
.app-nav-icon {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0.85;
}
.app-sidebar-foot {
  padding: 12px 18px 16px;
  border-top: 1px solid var(--p-line);
}
.foot-user {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--p-ink);
  font-size: 13px;
  margin-bottom: 4px;
}
.foot-user i { color: var(--p-blue); }
.foot-meta { color: var(--p-muted); font-size: 11px; }

/* ---------- 主区域 ---------- */
.app-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.app-topbar {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px;
  background: var(--p-surface);
  border-bottom: 1px solid var(--p-line);
  position: sticky;
  top: 0;
  z-index: 100;
}
.app-breadcrumb { font-size: 14px; color: var(--p-muted); }
.app-breadcrumb strong { color: var(--p-ink); font-weight: 500; }
.app-context { display: flex; align-items: center; gap: 10px; }
.ctx-label { color: var(--p-muted); font-size: 12px; }
.ctx-user {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--p-ink);
  font-size: 13px;
  padding: 6px 12px;
  background: var(--p-subtle);
  border: 1px solid var(--p-line);
  border-radius: 20px;
}
.role-tag { margin-left: 2px; }
.ctx-more { cursor: pointer; color: var(--p-muted); font-size: 14px; }
.ctx-more:hover { color: var(--p-blue); }
.app-content {
  flex: 1;
  min-width: 0;
  padding: 20px 24px 30px;
}
</style>
